import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Navigate, Link } from "react-router-dom";
import { Shield, Mail, CheckCircle2, Loader2, Heart, Sun, Moon, Search, FileText, BookOpen } from "lucide-react";
import CompanyAlbum from "../components/CompanyAlbum";
import ContactCard from "../components/ContactCard";
import LanguagePicker from "../components/LanguagePicker";
import PhoneSignIn from "../components/PhoneSignIn";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import logging from "../utils/logging";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [method, setMethod] = useState("email"); // email | phone

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError(t("login.invalid_email"));
      return;
    }
    setSending(true);
    setError("");
    // Referral pickup — the `/demo?ref=<code>` page stashes the code
    // in localStorage. Include it here so the backend can credit both
    // parties on the new-user's first sign-in. Clear it immediately
    // so a subsequent sign-in from the same device (which is really
    // just a different session, not a new user) doesn't re-attempt an
    // award (backend is idempotent, but keeping localStorage tidy).
    let ref = null;
    try {
      ref = localStorage.getItem("guard.ref") || null;
      if (ref) localStorage.removeItem("guard.ref");
    } catch { /* private mode — no referral tracking, not a big deal */ }
    try {
      await axios.post(
        `${API}/auth/magic-link`,
        { email: email.trim().toLowerCase(), ref },
        { withCredentials: true },
      );
      setSent(true);
    } catch (err) {
      logging.error("Magic-link request failed", err);
      setError(formatDetail(err.response?.data?.detail) || t("login.link_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="login-page">
      {/* Top section: branding + login form (matches viewport on desktop) */}
      <div className="flex min-h-screen">
        {/* Left panel - branding */}
        <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center bg-black overflow-hidden">
          {/* Subtle radial gradient for depth — no photographic background */}
          <div
            className="absolute inset-0 opacity-60"
            style={{
              background: "radial-gradient(circle at 30% 30%, rgba(220,38,38,0.18), transparent 60%), radial-gradient(circle at 70% 75%, rgba(59,130,246,0.12), transparent 55%)",
            }}
          />
          <div className="relative z-10 text-center px-12">
            <Shield className="w-20 h-20 text-white mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-heading text-5xl font-black text-white tracking-tighter mb-3">GUARD</h1>
            <p className="text-gray-400 font-body text-base mb-10">{t("login.subtitle")}</p>
            <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
              <div className="bg-red-500/20 border border-red-500/40 p-3 rounded-sm"><p className="text-red-400 text-xs uppercase tracking-[0.2em] font-bold">{t("emergency.security")}</p></div>
              <div className="bg-orange-500/20 border border-orange-500/40 p-3 rounded-sm"><p className="text-orange-400 text-xs uppercase tracking-[0.2em] font-bold">{t("emergency.fire")}</p></div>
              <div className="bg-blue-500/20 border border-blue-500/40 p-3 rounded-sm"><p className="text-blue-400 text-xs uppercase tracking-[0.2em] font-bold">{t("emergency.medical")}</p></div>
              <div className="bg-yellow-500/20 border border-yellow-500/40 p-3 rounded-sm"><p className="text-yellow-400 text-xs uppercase tracking-[0.2em] font-bold">{t("emergency.accident")}</p></div>
            </div>
          </div>
        </div>

      {/* Right panel - login */}
      <div className="flex-1 flex items-center justify-center bg-[#F3F4F6] px-8 relative">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <LanguagePicker />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-sm hover:bg-gray-200 text-gray-600 transition-colors"
            data-testid="theme-toggle-login"
            aria-label={theme === "dark" ? t("header.toggle_theme_light") : t("header.toggle_theme_dark")}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Shield className="w-10 h-10 text-black" strokeWidth={1.5} />
            <span className="font-heading text-3xl font-black tracking-tighter">GUARD</span>
          </div>

          {sent ? (
            <div className="bg-white border border-gray-200 rounded-sm p-6 text-center" data-testid="magic-link-sent">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">{t("login.link_sent_title")}</h2>
              <p className="text-sm text-gray-600 font-body mb-1">{t("login.link_sent_body")}</p>
              <p className="text-sm font-mono font-bold text-gray-900 mb-4">{email}</p>
              <p className="text-xs text-gray-400 font-body">{t("login.link_expires")}</p>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="mt-5 text-xs text-gray-500 font-body underline"
                data-testid="resend-or-change-email"
              >
                {t("login.different_email")}
              </button>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-2">{t("login.sign_in")}</h2>
              <p className="text-gray-500 font-body text-sm mb-6">{t("login.sign_in_hint")}</p>
              <div className="grid grid-cols-2 gap-1 bg-gray-200/70 rounded-sm p-1 mb-5" data-testid="login-method-toggle">
                <button
                  type="button"
                  onClick={() => setMethod("email")}
                  data-testid="login-method-email"
                  className={`h-9 rounded-sm text-xs font-body font-bold uppercase tracking-widest transition-colors ${method === "email" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {t("login.method_email")}
                </button>
                <button
                  type="button"
                  onClick={() => setMethod("phone")}
                  data-testid="login-method-phone"
                  className={`h-9 rounded-sm text-xs font-body font-bold uppercase tracking-widest transition-colors ${method === "phone" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {t("login.method_phone")}
                </button>
              </div>
              {method === "phone" ? (
                <PhoneSignIn />
              ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1 block">{t("login.email_label")}</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login.email_placeholder")}
                      className="rounded-sm h-12 text-sm pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-red-600 font-body" data-testid="login-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="send-magic-link-btn"
                  className="w-full bg-black hover:bg-gray-800 text-white rounded-sm h-12 font-body font-semibold text-sm tracking-wide"
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("login.sending")}</> : t("login.send_link")}
                </Button>
              </form>
              )}
              <div className="mt-6 text-center space-y-3">
                <p className="text-xs text-gray-400 font-body">
                  {t("login.admin_domains")}
                </p>
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Link
                    to="/lookup"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-black font-body font-semibold"
                    data-testid="login-lookup-link"
                  >
                    <Search className="w-4 h-4" /> {t("login.lookup_cta")}
                  </Link>
                  <p className="text-[11px] text-gray-400 font-body -mt-2">{t("login.lookup_hint")}</p>
                  <Link
                    to="/donate"
                    className="inline-flex items-center gap-1.5 text-sm text-rose-600 hover:text-rose-700 font-body font-semibold"
                    data-testid="login-donate-link"
                  >
                    <Heart className="w-4 h-4" fill="currentColor" /> {t("login.donate_cta")}
                  </Link>
                  <p className="text-[11px] text-gray-400 font-body mt-1">{t("login.donate_hint")}</p>
                </div>
                <div className="pt-3 flex items-center justify-center gap-4 text-[11px]">
                  <Link
                    to="/terms"
                    className="text-gray-500 hover:text-gray-900 font-body inline-flex items-center gap-1"
                    data-testid="login-terms-link"
                  >
                    <FileText className="w-3 h-3" /> {t("login.terms_link")}
                  </Link>
                  <span className="text-gray-300">·</span>
                  <a
                    href="/Guard_Technical_Paper.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-500 hover:text-gray-900 font-body inline-flex items-center gap-1"
                    data-testid="login-paper-link"
                  >
                    <BookOpen className="w-3 h-3" /> {t("login.paper_link")}
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>{/* end of top flex section */}

      <CompanyAlbum />
      <ContactCard />
    </div>
  );
}
